import { dbFirestore } from "firestore";
import useSWRImmutable from "swr/immutable";

import type { FeatureFlag } from "firestore/types";

type FeatureFlagKey =
  | "llhls"
  | "MMSForVOD"
  | "signedUrl"
  | "freePurchase"
  | "polling"
  | "commentPersonalBlock"
  | "useTokenForCast";

const DEFAULT_FLAG: FeatureFlag = {
  globalEnabled: false,
  events: {},
};

/**
 * Firestoreの featureFlags コレクションからフラグを取得し、
 * eventId に応じて有効/無効を判定する汎用hook。
 *
 * @param key Firestoreのドキュメントキー
 * @param eventId 対象のイベントID
 * @param urlOverrides URLパラメータによるオーバーライド設定
 */
export const useFeatureFlagFromFirestore = (
  key: FeatureFlagKey,
  eventId: string | undefined,
  urlOverrides?: { enable?: string; disable?: string }
): boolean | undefined => {
  const { data: flag } = useSWRImmutable<FeatureFlag>(
    `featureFlag:${key}`,
    async () => {
      try {
        const result = await dbFirestore.featureFlags().doc(key).get();
        const data = result.data();
        const returnValue = data ?? DEFAULT_FLAG;

        return returnValue;
      } catch {
        return DEFAULT_FLAG;
      }
    }
  );

  try {
    if (urlOverrides && typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (urlOverrides.enable && params.get(urlOverrides.enable) === "1") {
        return true;
      }
      if (urlOverrides.disable && params.get(urlOverrides.disable) === "1") {
        return false;
      }
    }

    if (flag === undefined) {
      return undefined;
    }

    if (flag.globalEnabled) {
      return true;
    }
    if (eventId && flag.events[eventId]) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};
